import { FarmlyPlace } from "@/lib/types";
import { fetchPlacesMock, fetchPlaceBySlugMock } from "./mock";
import {
  fetchPlacesSupabase,
  fetchPlaceBySlugSupabase,
  hasSupabaseConfig,
} from "./supabase";

export type PlacesDataSource = "mock" | "supabase";

type DataSourceConfig = "mock" | "supabase" | "auto";

/**
 * Reads the data source config from environment.
 * Defaults to "auto" if not set.
 */
function getDataSourceConfig(): DataSourceConfig {
  const env = import.meta.env.VITE_DATA_SOURCE as string | undefined;
  if (env === "mock" || env === "supabase") {
    return env;
  }
  return "auto";
}

export type ResolveResult = {
  source: PlacesDataSource;
  reason: string;
};

/**
 * Determines which data source to use based on config and available env vars.
 */
export function resolvePlacesDataSource(): ResolveResult {
  const config = getDataSourceConfig();

  if (config === "mock") {
    return { source: "mock", reason: "VITE_DATA_SOURCE=mock" };
  }

  if (config === "supabase") {
    if (hasSupabaseConfig()) {
      return { source: "supabase", reason: "VITE_DATA_SOURCE=supabase" };
    }
    // Supabase requested but env missing - will fallback to mock with error
    return {
      source: "mock",
      reason: "VITE_DATA_SOURCE=supabase men env saknas, fallback till mock",
    };
  }

  // Auto mode: use supabase if config exists, otherwise mock
  if (hasSupabaseConfig()) {
    return { source: "supabase", reason: "Auto: Supabase-env hittades" };
  }

  return { source: "mock", reason: "Auto: Ingen Supabase-env, använder mock" };
}

export type GetPlacesResult = {
  places: FarmlyPlace[];
  source: PlacesDataSource;
  error?: string;
};

/**
 * Fetches places from the appropriate data source.
 * Handles auto-detection and fallback logic.
 */
export async function getPlaces(): Promise<GetPlacesResult> {
  const config = getDataSourceConfig();
  const resolved = resolvePlacesDataSource();

  // If supabase was explicitly requested but env is missing
  if (config === "supabase" && !hasSupabaseConfig()) {
    const mockPlaces = await fetchPlacesMock();
    return {
      places: mockPlaces,
      source: "mock",
      error: "Supabase-konfiguration saknas – visar mock-data.",
    };
  }

  if (resolved.source === "supabase") {
    const result = await fetchPlacesSupabase();

    // If Supabase fetch failed, fallback to mock
    if (result.error || result.places.length === 0) {
      const mockPlaces = await fetchPlacesMock();
      return {
        places: mockPlaces,
        source: "mock",
        error: result.error || "Inga platser hittades i Supabase.",
      };
    }

    return {
      places: result.places,
      source: "supabase",
    };
  }

  // Mock mode
  const mockPlaces = await fetchPlacesMock();
  return {
    places: mockPlaces,
    source: "mock",
  };
}

export type GetPlaceBySlugResult = {
  place: FarmlyPlace | null;
  source: PlacesDataSource;
  error?: string;
};

/**
 * Fetches a single place by slug from the appropriate data source.
 */
export async function getPlaceBySlug(
  slug: string
): Promise<GetPlaceBySlugResult> {
  const config = getDataSourceConfig();
  const resolved = resolvePlacesDataSource();

  // If supabase was explicitly requested but env is missing
  if (config === "supabase" && !hasSupabaseConfig()) {
    const place = await fetchPlaceBySlugMock(slug);
    return {
      place,
      source: "mock",
      error: "Supabase-konfiguration saknas – visar mock-data.",
    };
  }

  if (resolved.source === "supabase") {
    const result = await fetchPlaceBySlugSupabase(slug);

    // If Supabase fetch failed, fallback to mock
    if (result.error) {
      const place = await fetchPlaceBySlugMock(slug);
      return {
        place,
        source: "mock",
        error: result.error,
      };
    }

    return {
      place: result.place,
      source: "supabase",
    };
  }

  // Mock mode
  const place = await fetchPlaceBySlugMock(slug);
  return {
    place,
    source: "mock",
  };
}
